import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import { ScrollFadeIn } from './utils/Utils';
import { HomepageReview } from '../../types';

type ReviewsSectionProps = {
  reviews?: HomepageReview[];
};

const REVIEW_TEXT_CHARS_PER_LINE = 42;
const REVIEW_CARD_SHORT_HEIGHT_PX = 260;
const REVIEW_CARD_TALL_HEIGHT_PX = 360;

const normalizeReviewerName = (rawName: string): string => {
  const parts = rawName
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];

  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${firstName} ${lastInitial}.`;
};

const StarRating: React.FC<{ value: number }> = ({ value }) => (
  <Stack direction="row" spacing={0.25}>
    {[1, 2, 3, 4, 5].map((i) => (
      i <= value
        ? <StarRoundedIcon key={i} sx={{ fontSize: 16, color: 'secondary.main' }} />
        : <StarBorderRoundedIcon key={i} sx={{ fontSize: 16, color: 'action.disabled' }} />
    ))}
  </Stack>
);

const REVIEW_CARD_WIDTH = 340;

// ─── Quote mark ──────────────────────────────────────────────────────────────

const QuoteMark: React.FC<{ opacity?: number }> = ({ opacity = 1 }) => (
  <svg
    width="48"
    height="36"
    viewBox="0 0 48 36"
    fill="none"
    style={{ opacity }}
  >
    <path
      d="M0 36V22.5C0 14.4 3 8.1 9 3.6L13.5 7.2C10.2 9.9 8.1 13.5 7.5 18H15V36H0ZM27 36V22.5C27 14.4 30 8.1 36 3.6L40.5 7.2C37.2 9.9 35.1 13.5 34.5 18H42V36H27Z"
      fill="currentColor"
    />
  </svg>
);

// ─── Initials Avatar ─────────────────────────────────────────────────────────

const InitialsAvatar: React.FC<{ name: string }> = ({ name }) => {
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.95rem',
          fontWeight: 700,
          color: 'text.primary',
          lineHeight: 1,
        }}
      >
        {initials}
      </Typography>
    </Box>
  );
};

// ─── Review Card ─────────────────────────────────────────────────────────────

type ReviewCardProps = {
  review: HomepageReview;
  cardHeight: number;
};

const ReviewCard: React.FC<ReviewCardProps> = ({ review, cardHeight }) => (
  <Box
    sx={{
      width: '100%',
      minWidth: 0,
      position: 'relative',
      height: `${cardHeight}px`,
      borderRadius: '16px',
      overflow: 'hidden',
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      p: { xs: 3, md: 3.5 },
      display: 'flex',
      flexDirection: 'column',
      gap: 2.5,
      transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
      boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
      '&:hover': {
        boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
      },
    }}
  >
    {/* Subtle glow top-left */}
    <Box
      sx={{
        position: 'absolute',
        top: -40,
        left: -40,
        width: 140,
        height: 140,
        borderRadius: '50%',
        bgcolor: 'action.hover',
        filter: 'blur(40px)',
        pointerEvents: 'none',
      }}
    />

    {/* Quote */}
    <Box sx={{ color: 'secondary.main', opacity: 0.35 }}>
      <QuoteMark />
    </Box>

    {/* Comment */}
    <Typography
      sx={{
        fontSize: { xs: '1rem', md: '1.05rem' },
        fontWeight: 400,
        color: 'text.primary',
        lineHeight: 1.7,
        flex: 1,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: cardHeight === REVIEW_CARD_TALL_HEIGHT_PX ? 6 : 4,
      }}
    >
      {review.text}
    </Typography>

    {/* Footer */}
    <Box
      sx={{
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <InitialsAvatar name={review.name} />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: 'text.primary',
            letterSpacing: 0.3,
            textAlign: 'center',
          }}
        >
          {review.name}
        </Typography>
        <Box mt={0.4}>
          <StarRating value={Math.max(1, Math.min(5, review.rating ?? 5))} />
        </Box>
      </Box>
    </Box>
  </Box>
);

// ─── Navigation Dots ─────────────────────────────────────────────────────────

const NavDots: React.FC<{
  count: number;
  active: number;
  onDotClick: (i: number) => void;
}> = ({ count, active, onDotClick }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
    {Array.from({ length: count }).map((_, i) => (
      <Box
        key={i}
        onClick={() => onDotClick(i)}
        sx={{
          width: i === active ? 24 : 6,
          height: 6,
          borderRadius: '3px',
          bgcolor: i === active ? 'secondary.main' : 'action.disabled',
          cursor: 'pointer',
          transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
          '&:hover': { bgcolor: i === active ? 'secondary.main' : 'text.disabled' },
        }}
      />
    ))}
  </Stack>
);

// ─── Section ─────────────────────────────────────────────────────────────────

const ReviewsSection: React.FC<ReviewsSectionProps> = ({ reviews }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const reviewList: HomepageReview[] = useMemo(
    () => (reviews ?? [])
      .filter((review) => review?.name?.trim() && review?.text?.trim())
      .map((review) => ({
        ...review,
        name: normalizeReviewerName(review.name),
        text: review.text.trim(),
        rating: Math.max(1, Math.min(5, review.rating ?? 5)),
      })),
    [reviews]
  );

  const uniformCardHeight = useMemo(() => {
    if (reviewList.length === 0) return REVIEW_CARD_SHORT_HEIGHT_PX;

    const longestReviewLength = Math.max(
      ...reviewList.map((review) => review.text.length)
    );
    const estimatedLineCount = Math.ceil(longestReviewLength / REVIEW_TEXT_CHARS_PER_LINE);
    return estimatedLineCount > 4 ? REVIEW_CARD_TALL_HEIGHT_PX : REVIEW_CARD_SHORT_HEIGHT_PX;
  }, [reviewList]);

  if (reviewList.length === 0) {
    return null;
  }

  return (
    <>
      <style>{`
        #reviews-swiper .swiper-pagination {
          display: none;
        }
        #reviews-swiper .swiper,
        #reviews-swiper .swiper-wrapper,
        #reviews-swiper .swiper-slide {
          height: 100% !important;
        }
      `}</style>

      <Box
        id="reviews"
        sx={{
          position: 'relative',
          px: { xs: 2, md: 2 },
          pt: { xs: 6, md: 8 },
          pb: { xs: 7, md: 9 },
          overflow: 'visible',
        }}
      >
        {/* Background atmosphere */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at 15% 50%, rgba(0,0,0,0.04) 0%, transparent 60%), radial-gradient(ellipse at 85% 20%, rgba(0,0,0,0.03) 0%, transparent 55%)',
            pointerEvents: 'none',
          }}
        />

        <ScrollFadeIn>
          <Box maxWidth="lg" mx="auto">
            {/* ── Header ── */}
            <Box mb={6} textAlign="center">
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                What people are saying
              </Typography>
            </Box>

            {/* ── Swiper ── */}
            <Box id="reviews-swiper">
              <Swiper
                modules={[Pagination, Autoplay]}
                spaceBetween={20}
                slidesPerView="auto"
                autoHeight
                pagination={{ clickable: true }}
                autoplay={{ delay: 4000, disableOnInteraction: false }}
                onSwiper={(swiper: SwiperType) => { swiperRef.current = swiper; }}
                onSlideChange={(swiper: SwiperType) => setActiveIndex(swiper.realIndex)}
                breakpoints={{
                  640: { spaceBetween: 16 },
                  900: { spaceBetween: 20 },
                  1200: { spaceBetween: 24 },
                }}
                style={{ paddingBottom: 18, paddingLeft: 2, paddingRight: 2 }}
              >
                {reviewList.map((review, i) => (
                  <SwiperSlide
                    key={`${review.name}-${i}`}
                    style={{
                      height: '100%',
                      display: 'flex',
                      width: `${REVIEW_CARD_WIDTH}px`,
                      minWidth: `${REVIEW_CARD_WIDTH}px`,
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ py: 1, width: '100%', display: 'flex' }}>
                      <ReviewCard
                        review={review}
                        cardHeight={uniformCardHeight}
                      />
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>

            {/* ── Custom nav dots ── */}
            <Box mt={3.5}>
              <NavDots
                count={reviewList.length}
                active={activeIndex}
                onDotClick={(i) => swiperRef.current?.slideTo(i)}
              />
            </Box>
          </Box>
        </ScrollFadeIn>
      </Box>
    </>
  );
};

export default ReviewsSection;
